/**
 * Shared presentational form body for editing a contact.
 * Renders name fields, phone section, listing categories, and "Add Field" action sheet.
 * No toolbar or modal wrapper — those are provided by the parent.
 */

import React, { useState } from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonIcon,
  IonActionSheet,
  IonAlert,
} from '@ionic/react';
import { addCircle, removeCircle } from 'ionicons/icons';
import { PhoneLogic } from '@/modules/phone';
import type { PhoneTypeKind } from '@/modules/phone';
import PhoneInput from '@/components/PhoneInput';
import SectionCard from '@/components/SectionCard';
import type { ContactEditFormState } from './useContactEditForm';
import { pxToRem } from '@/modules/config/constants';

interface ContactEditFormBodyProps extends ContactEditFormState {
  isAddMode?: boolean;
  allListingCategories: string[];
}

const ContactEditFormBody: React.FC<ContactEditFormBodyProps> = ({
  prefix,
  setPrefix,
  firstName,
  setFirstName,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  suffix,
  setSuffix,
  nickname,
  setNickname,
  companyName,
  setCompanyName,
  tels,
  readOnlyTels,
  phoneInputs,
  handlePhoneChange,
  handlePhoneTypeChange,
  handleAddPhone,
  handleRemovePhone,
  handleRemoveReadOnlyPhone,
  listingCategories,
  handleAddListingCategory,
  handleRemoveListingCategory,
  allListingCategories,
  visibleFields,
  showAddFieldSheet,
  setShowAddFieldSheet,
  handleAddField,
  getAvailableFields,
  hasValidPhone,
  isAddMode = false,
}) => {
  const [showAddToListSheet, setShowAddToListSheet] = useState(false);
  const [showNewListAlert, setShowNewListAlert] = useState(false);

  // Merge global categories with form-local ones (e.g. newly created lists not yet saved)
  const availableCategories = [
    ...Array.from(new Set([...allListingCategories, ...listingCategories])),
  ].sort((a, b) => a.localeCompare(b));

  const addToListButtons = [
    ...availableCategories.map(cat => {
      const alreadyAssigned = listingCategories.includes(cat);
      return {
        text: cat,
        cssClass: alreadyAssigned ? 'action-sheet-disabled' : '',
        handler: () => {
          if (!alreadyAssigned) handleAddListingCategory(cat);
          return !alreadyAssigned;
        },
      };
    }),
    {
      text: 'New List...',
      handler: () => {
        setShowAddToListSheet(false);
        setTimeout(() => setShowNewListAlert(true), 300);
        return true;
      },
    },
    { text: 'Cancel', role: 'cancel' as const },
  ];

  const nameFields = [
    visibleFields.has('prefix') && (
      <IonItem key="prefix">
        <IonLabel position="stacked">Prefix</IonLabel>
        <IonInput
          value={prefix}
          onIonInput={e => setPrefix(e.detail.value || '')}
          placeholder="Mr., Mrs., Dr., etc."
        />
      </IonItem>
    ),
    <IonItem key="firstName">
      <IonLabel position="stacked" className="contact-edit-form__name-label">
        First Name
      </IonLabel>
      <IonInput
        value={firstName}
        onIonInput={e => setFirstName(e.detail.value || '')}
        placeholder="First name"
      />
    </IonItem>,
    visibleFields.has('middleName') && (
      <IonItem key="middleName">
        <IonLabel position="stacked">Middle Name</IonLabel>
        <IonInput
          value={middleName}
          onIonInput={e => setMiddleName(e.detail.value || '')}
          placeholder="Middle name"
        />
      </IonItem>
    ),
    <IonItem key="lastName">
      <IonLabel position="stacked" className="contact-edit-form__name-label">
        Last Name
      </IonLabel>
      <IonInput
        value={lastName}
        onIonInput={e => setLastName(e.detail.value || '')}
        placeholder="Last name"
      />
    </IonItem>,
    visibleFields.has('suffix') && (
      <IonItem key="suffix">
        <IonLabel position="stacked">Suffix</IonLabel>
        <IonInput
          value={suffix}
          onIonInput={e => setSuffix(e.detail.value || '')}
          placeholder="Jr., Sr., III, etc."
        />
      </IonItem>
    ),
    visibleFields.has('nickname') && (
      <IonItem key="nickname">
        <IonLabel position="stacked">Nickname</IonLabel>
        <IonInput
          value={nickname}
          onIonInput={e => setNickname(e.detail.value || '')}
          placeholder="Nickname"
        />
      </IonItem>
    ),
    visibleFields.has('companyName') && (
      <IonItem key="companyName">
        <IonLabel position="stacked">Company</IonLabel>
        <IonInput
          value={companyName}
          onIonInput={e => setCompanyName(e.detail.value || '')}
          placeholder="Company"
          data-testid="contact-company-input"
        />
      </IonItem>
    ),
  ].filter(Boolean);

  const phoneTypeOptions = PhoneLogic.PHONE_TYPE_KINDS.map(kind => (
    <IonSelectOption key={kind} value={kind}>
      {PhoneLogic.getPhoneTypeLabel(
        kind === 'other'
          ? PhoneLogic.makeOther('other')
          : PhoneLogic.PHONE_TYPES[kind]
      )}
    </IonSelectOption>
  ));

  return (
    <div className="contact-edit-form" style={{ padding: '20px' }}>
      {/* Name Fields */}
      <SectionCard>{nameFields}</SectionCard>

      {/* Phone Numbers */}
      <div style={{ marginTop: '20px' }}>
        <IonLabel></IonLabel>
        {isAddMode && !hasValidPhone && (
          <p
            style={{
              fontSize: pxToRem(13),
              color: '#999',
              margin: '0 0 8px 16px',
            }}
          >
            A valid phone number is required
          </p>
        )}
        <SectionCard>
          {tels.map(tel => (
            <IonItem key={tel.localId} lines="inset">
              <IonButton
                slot="start"
                fill="clear"
                onClick={() => handleRemovePhone(tel.localId)}
                style={{ minWidth: 'auto', '--color': '#ff3b30' }}
                aria-label="Remove phone"
              >
                <IonIcon slot="icon-only" icon={removeCircle} />
              </IonButton>
              <IonSelect
                value={PhoneLogic.getKind(tel.type)}
                interface="action-sheet"
                onIonChange={e =>
                  handlePhoneTypeChange(
                    tel.localId,
                    e.detail.value as PhoneTypeKind
                  )
                }
                style={
                  {
                    maxWidth: '112px',
                    '--padding-start': '0',
                    '--padding-end': '20px',
                  } as React.CSSProperties
                }
              >
                {phoneTypeOptions}
              </IonSelect>
              <PhoneInput
                value={phoneInputs[tel.localId] || ''}
                onPhoneChange={value => handlePhoneChange(tel.localId, value)}
                placeholder="(555) 123-4567"
              />
            </IonItem>
          ))}
          {readOnlyTels.map(tel => (
            <IonItem key={tel.localId} lines="inset" style={{ opacity: 0.6 }}>
              <IonButton
                slot="start"
                fill="clear"
                onClick={() => handleRemoveReadOnlyPhone(tel.localId)}
                style={{ minWidth: 'auto', '--color': '#ff3b30' }}
                aria-label="Remove phone"
              >
                <IonIcon slot="icon-only" icon={removeCircle} />
              </IonButton>
              <IonLabel position="stacked">
                {PhoneLogic.getPhoneTypeLabel(tel.type)} (non-US)
              </IonLabel>
              <IonInput value={tel.value} readonly />
            </IonItem>
          ))}
          <IonItem button lines="none" onClick={handleAddPhone}>
            <IonIcon
              slot="start"
              icon={addCircle}
              className="contact-edit-form__add-action-icon"
            />
            <IonLabel
              color="secondary"
              className="contact-edit-form__add-action-label"
              style={{ marginInlineStart: '6px' }}
            >
              Add phone
            </IonLabel>
          </IonItem>
        </SectionCard>
      </div>

      {/* Included in Lists */}
      <div
        style={{ marginTop: '20px' }}
        data-testid="included-in-lists-edit-section"
      >
        <SectionCard title="Included in Lists">
          {listingCategories.map((category, index) => (
            <IonItem key={index} lines="inset">
              <IonButton
                slot="start"
                fill="clear"
                color="danger"
                size="small"
                onClick={() => handleRemoveListingCategory(index)}
                aria-label="Remove from list"
                data-testid={`edit-remove-from-list-${index}`}
              >
                <IonIcon slot="icon-only" icon={removeCircle} />
              </IonButton>
              <IonLabel>{category}</IonLabel>
            </IonItem>
          ))}
          <IonItem
            button
            lines="none"
            onClick={() => setShowAddToListSheet(true)}
            data-testid="edit-add-to-list-button"
          >
            <IonIcon
              slot="start"
              icon={addCircle}
              className="contact-edit-form__add-action-icon"
            />
            <IonLabel
              color="secondary"
              className="contact-edit-form__add-action-label contact-edit-form__add-to-list-button"
              style={{ marginInlineStart: '6px' }}
            >
              Add to List
            </IonLabel>
          </IonItem>
        </SectionCard>

        {showAddToListSheet && (
          <IonActionSheet
            isOpen={showAddToListSheet}
            onDidDismiss={() => setShowAddToListSheet(false)}
            header="Add to List"
            buttons={addToListButtons}
          />
        )}

        {showNewListAlert && (
          <IonAlert
            isOpen={showNewListAlert}
            onDidDismiss={() => setShowNewListAlert(false)}
            header="New List"
            inputs={[
              { name: 'listName', type: 'text', placeholder: 'List name' },
            ]}
            buttons={[
              { text: 'Cancel', role: 'cancel' },
              {
                text: 'OK',
                handler: (data: { listName: string }) => {
                  handleAddListingCategory(data.listName || '');
                },
              },
            ]}
          />
        )}
      </div>

      {/* Add Field Button */}
      {getAvailableFields().length > 0 && (
        <div
          role="button"
          tabIndex={0}
          className="contact-edit-form__add-field-trigger"
          onClick={() => setShowAddFieldSheet(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowAddFieldSheet(true);
            }
          }}
        >
          <span className="contact-edit-form__add-field-label">Add field</span>
        </div>
      )}

      {/* Add Field Action Sheet */}
      <IonActionSheet
        isOpen={showAddFieldSheet}
        onDidDismiss={() => setShowAddFieldSheet(false)}
        header="Add Field"
        buttons={[
          ...getAvailableFields().map(field => ({
            text: field.label,
            handler: () => handleAddField(field.key),
          })),
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />
    </div>
  );
};

export default ContactEditFormBody;
